import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { verificationApi, deliverablesApi, agreementsApi } from '../utils/api';
import AgentVerdictBadge from '../components/AgentVerdictBadge';

// design-spec-evidence-review.md — Surface B.
//
// Dedicated, role-symmetric evidence record for one Deliverable, reachable from
// every "View full evidence" link on the Agreement page. Both seller and buyer
// of the Agreement may read it (the GET .../evidence endpoint enforces side).
//
// Data sources (all read-only; no settlement logic touched):
//   - verificationApi.listEvidence  → outcome_evidence rows (agent / human)
//   - deliverablesApi.listEvents    → deliverable_events (manual-override note)
//   - agreementsApi.get             → names for the breadcrumb
//
// States: agent/human evidence · manual-override-only (no artifacts) · empty.

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function VERDICT_RANK(v) {
  return { contradicted: 3, needs_review: 2, unverified: 1, verified: 0 }[v] ?? 2;
}

export default function DeliverableEvidence() {
  const { id, deliverableId } = useParams();
  const [evidence, setEvidence] = useState([]);
  const [events, setEvents] = useState([]);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [evRes, evtRes, agRes] = await Promise.all([
          verificationApi.listEvidence(id, deliverableId),
          deliverablesApi.listEvents(id, deliverableId).catch(() => ({ data: {} })),
          agreementsApi.get(id).catch(() => ({ data: {} })),
        ]);
        if (!mounted) return;
        setEvidence(evRes.data?.evidence || []);
        setEvents(evtRes.data?.events || evtRes.data || []);
        setAgreement(agRes.data?.agreement || agRes.data || null);
      } catch (e) {
        if (!mounted) return;
        setError(
          e?.response?.data?.error ||
          e?.message ||
          'Failed to load evidence'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, deliverableId]);

  // Names for the breadcrumb.
  let outcomeName = null;
  let deliverableName = null;
  for (const o of (agreement?.outcomes || [])) {
    for (const d of (o.deliverables || [])) {
      if (d.id === deliverableId) { outcomeName = o.name; deliverableName = d.name; }
    }
  }

  // Manual-override event (verified with no artifacts).
  const overrideEvent = (events || []).find(
    (ev) => ev.event_type === 'status_changed'
      && ev.to_status === 'verified'
      && (ev.payload_json?.via === 'manual')
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Evidence not available</h1>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <Link to={`/agreements/${id}`} className="text-sm text-primary-600 hover:text-primary-700">
            ← Back to agreement
          </Link>
        </div>
      </div>
    );
  }

  const hasArtifacts = evidence.length > 0;
  // Overall verdict = worst-case across the rows.
  const overallVerdict = hasArtifacts
    ? evidence.reduce((worst, e) => (VERDICT_RANK(e.verdict) > VERDICT_RANK(worst) ? e.verdict : worst), 'verified')
    : null;
  const sources = Array.from(new Set(evidence.map((e) => e.source_system).filter(Boolean)));
  const latestCaptured = evidence
    .map((e) => e.fetched_at || e.created_at)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-4xl mx-auto px-4">
        <Link to={`/agreements/${id}`} className="text-sm text-gray-600 hover:text-gray-800">
          ← Back to Agreement{agreement?.name ? ` · ${agreement.name}` : ''}
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mt-3 mb-4">
          <div>
            <p className="text-xs text-gray-600">
              {agreement?.name && <span className="font-medium text-gray-700">{agreement.name}</span>}
              {outcomeName && <> · Outcome <span className="font-medium text-gray-700">{outcomeName}</span></>}
              {deliverableName && <> · Deliverable <span className="font-medium text-gray-700">{deliverableName}</span></>}
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mt-1">Evidence</h1>
            <p className="text-sm text-gray-600 mt-1">
              {hasArtifacts
                ? 'Verified against the spec locked at signing · shared with both parties'
                : overrideEvent
                  ? 'Confirmed manually by the agency · no artifacts on file · shared with both parties'
                  : 'Awaiting verification · shared with both parties'}
            </p>
          </div>
          <div className="text-right">
            {hasArtifacts
              ? <AgentVerdictBadge verdict={overallVerdict} />
              : <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-gray-100 text-gray-600 border-gray-200">
                  {overrideEvent ? 'Verified (override)' : 'Awaiting'}
                </span>}
            <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mt-2">Spec version</div>
            <div className="text-sm font-semibold text-gray-800">v1 · immutable</div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SummaryCard k="Verdict" v={hasArtifacts ? (overallVerdict || '—') : (overrideEvent ? 'Override' : '—')} />
          <SummaryCard k="Artifacts on file" v={String(evidence.length)} />
          <SummaryCard k="Source" v={hasArtifacts ? (sources.join(', ') || '—') : (overrideEvent ? 'Manual' : '—')} />
          <SummaryCard k="Captured" v={hasArtifacts ? fmtDateTime(latestCaptured) : (overrideEvent ? fmtDateTime(overrideEvent.created_at) : '—')} />
        </div>

        <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-2">
          Evidence log · append-only · newest first
        </div>

        {/* Body */}
        {hasArtifacts ? (
          <div className="space-y-3">
            {evidence.map((e) => (
              <article key={e.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-wrap">
                  <AgentVerdictBadge verdict={e.verdict} />
                  <span className="font-semibold text-gray-900 text-sm">
                    {e.external_ref || e.artifact_requirement_id || 'Evidence artifact'}
                  </span>
                  <span className="ml-auto inline-flex items-center px-2 py-0.5 text-xs rounded border bg-gray-50 text-gray-600 border-gray-200">
                    {e.source_system || 'source'}
                  </span>
                  {e.external_url && (
                    <a href={e.external_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                      Open source ↗
                    </a>
                  )}
                </div>
                <div className="grid md:grid-cols-[1fr_280px] gap-5 px-4 py-4">
                  <div>
                    <h4 className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1.5">Rationale</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {e.verdict_rationale_markdown || 'No rationale recorded.'}
                    </p>
                    {e.raw_payload_json && (
                      <pre className="mt-3 bg-gray-900 text-gray-200 rounded-lg p-3 text-[11px] overflow-auto">
                        {JSON.stringify(e.raw_payload_json, null, 2)}
                      </pre>
                    )}
                  </div>
                  <dl className="text-xs text-gray-600 md:border-l md:border-gray-100 md:pl-4 space-y-1.5">
                    <MetaRow k="Source" v={e.source_system || '—'} />
                    <MetaRow k="Verdict" v={e.verdict} />
                    <MetaRow k="Satisfies" v={e.criterion || e.artifact_requirement_id || '—'} />
                    <MetaRow k="Spec version" v="v1 (locked)" />
                    <MetaRow k="Captured" v={fmtDateTime(e.fetched_at || e.created_at)} />
                    <MetaRow k="Content hash" v={e.content_hash ? `sha256:${e.content_hash}` : '—'} mono />
                  </dl>
                </div>
              </article>
            ))}
          </div>
        ) : overrideEvent ? (
          <article className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded border bg-gray-100 text-gray-600 border-gray-200">
                manual override
              </span>
              <span className="font-semibold text-gray-900 text-sm">Manual confirmation by the agency</span>
              {overrideEvent.actor_attribution_json?.name && (
                <span className="ml-auto inline-flex items-center px-2 py-0.5 text-xs rounded border bg-gray-50 text-gray-600 border-gray-200">
                  {overrideEvent.actor_attribution_json.name}
                </span>
              )}
            </div>
            <div className="px-4 py-4">
              <h4 className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1.5">Override note</h4>
              <p className="text-sm text-gray-700">
                {overrideEvent.payload_json?.note
                  ? `“${overrideEvent.payload_json.note}”`
                  : 'No note recorded.'}
              </p>
              <p className="text-xs text-gray-600 mt-3">
                No artifact was submitted to the agent. This Deliverable was advanced
                {' '}confirmed manually by the agency and recorded in the append-only event log
                {' '}on {fmtDateTime(overrideEvent.created_at)}.
              </p>
            </div>
          </article>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-6 text-sm text-gray-600">
            <span className="font-semibold text-gray-700">No evidence yet.</span>{' '}
            This page populates the moment the agency provides evidence or the Verdict
            agent runs verification.
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ k, v }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold">{k}</div>
      <div className="text-base font-semibold text-gray-900 mt-0.5 break-words">{v}</div>
    </div>
  );
}

function MetaRow({ k, v, mono = false }) {
  return (
    <div className="flex justify-between gap-3 border-b border-dashed border-gray-100 last:border-b-0 py-1">
      <dt className="text-gray-600 font-semibold whitespace-nowrap">{k}</dt>
      <dd className={`text-right text-gray-800 ${mono ? 'font-mono text-[11px] break-all' : ''}`}>{v}</dd>
    </div>
  );
}
